import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';
import { useAppSelector } from '../../../hooks/redux';
import { useDateLocalization } from '../../../hooks/useDateLocalization';
import { useCustomSnackbar } from '../../../hooks/useCustomEnqueueSnackbar';
import { CustomSkeletonElement } from '../../commons/CustomSkeletonElement';
import { CustomMUIButton } from '../../commons/buttons/CustomMUIButton';
import { userInfoApi } from '../../../services/apiServices/user/userInfo';
import { ButtonClassNames } from '../../../models/Common';
import { IUserDocument } from '../../../models/UserInfo';
import { ReactComponent as FileDownloadIcon } from '../../../assets/images/IconDownloadFile.svg';
import { getErrorMessage } from '../../../utils';

export const Documents: FC = () => {
  const { t } = useTranslation();
  const { formatDate } = useDateLocalization();
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const isMobile = useMediaQuery('(max-width:1279px)');
  const { selectedLanguage } = useAppSelector((state) => state.langReducer);
  const { data, isLoading, isError, error } = userInfoApi.useGetUserDocumentsQuery(selectedLanguage, {
    refetchOnMountOrArgChange: true,
  });
  const [downloadFile, { isFetching: isDownloading }] = userInfoApi.useLazyDownloadUserDocumentQuery();
  const [showFile, { isFetching: isShowing }] = userInfoApi.useLazyShowUserDocumentQuery();

  useEffect(() => {
    if (isError && error) {
      const message = getErrorMessage(error);
      customEnqueueSnackbar(message, 'error');
    }
  }, [isError, error]);

  const showFileHandler = async (url: string) => {
    const newTab = window.open('', '_blank');
    try {
      const fileData = await showFile(url.split('/').pop() as string).unwrap();
      const blob = new Blob([fileData], { type: 'application/pdf' });
      const objURL = window.URL.createObjectURL(blob);
      if (newTab) newTab.location.href = objURL;
      setTimeout(() => window.URL.revokeObjectURL(objURL), 8000);
    } catch (e) {
      newTab?.close();
      const message = getErrorMessage(e);
      customEnqueueSnackbar(message, 'error');
    }
  };

  const downloadFileHandler = async (url: string, name: string) => {
    try {
      const fileData = await downloadFile(url.split('/').pop() as string).unwrap();
      const blob = new Blob([fileData], { type: 'application/pdf' });
      const objURL = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = objURL;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(objURL);
    } catch (e) {
      const message = getErrorMessage(e);
      customEnqueueSnackbar(message, 'error');
    }
  };

  const getTableHead = () => {
    return (
      <div className="documents__table-row">
        <div className="documents__table-cell documents__table-th documents__table-id">№</div>
        <div className="documents__table-cell documents__table-th">{t('accountSettings.documentName')}</div>
        <div className="documents__table-cell documents__table-th documents__table-create-date">
          {t('accountSettings.documentCreateDate')}
        </div>
        <div className="documents__table-cell documents__table-th documents__table-download">
          {t('accountSettings.documentDownload')}
        </div>
      </div>
    );
  };

  const getTableBody = (item: IUserDocument, index: number) => {
    return (
      <div className="documents__table-row" key={item.id}>
        <div className="documents__table-cell documents__table-id">{index + 1}</div>
        <div className="documents__table-cell">
          <button
            type="button"
            className="documents__table-doc-name"
            onClick={() => showFileHandler(item.url.show)}
            disabled={isShowing}
          >
            {item.name}.pdf
          </button>
        </div>
        <div className="documents__table-cell documents__table-create-date">
          {formatDate(item.date, { day: '2-digit', month: '2-digit', year: 'numeric', locale: 'uk' })}
        </div>
        <div className="documents__table-cell documents__table-download">
          <CustomMUIButton
            label={<FileDownloadIcon />}
            handleClick={!isDownloading ? () => downloadFileHandler(item.url.download, item.name) : undefined}
            className={ButtonClassNames.IconGrayFulfilled}
            tooltipText={`${t('accountSettings.documentDownload')}`}
          />
        </div>
      </div>
    );
  };

  const getMobileTableBody = (item: IUserDocument) => {
    return (
      <div className="documents__table-item" key={item.id}>
        <div className="documents__table-info">
          <button type="button" onClick={() => showFileHandler(item.url.show)} disabled={isShowing}>
            {item.name}.pdf
          </button>
          <div className="documents__table-created-at">
            <span>{t('accountSettings.documentCreateDate')}</span>{' '}
            {formatDate(item.date, { day: '2-digit', month: '2-digit', year: 'numeric', locale: 'uk' })}
          </div>
        </div>
        <div className="documents__table-upload">
          <CustomMUIButton
            label={<FileDownloadIcon />}
            handleClick={!isDownloading ? () => downloadFileHandler(item.url.download, item.name) : undefined}
            className={ButtonClassNames.IconGrayFulfilled}
            tooltipText={`${t('accountSettings.documentDownload')}`}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="documents">
      {(() => {
        if (isLoading) {
          return (
            !isMobile && (
              <CustomSkeletonElement
                skeletonContainerClassName="skeleton-container"
                skeletonChildrenClassName="skeleton-children skeleton"
                childrenCount={1}
              />
            )
          );
        }
        if (!isLoading && data && data.length) {
          return (
            <div className="documents__table">
              {!isMobile && getTableHead()}
              {data.map((doc, index) => {
                return !isMobile ? getTableBody(doc, index) : getMobileTableBody(doc);
              })}
            </div>
          );
        }
        if (isError) {
          return <p className="documents__no-documents">{t('paymentHistory.errorOccurred')}</p>;
        }
        return <p className="documents__no-documents">{t('accountSettings.noDocumentsYet')}</p>;
      })()}
    </div>
  );
};
